import { useEffect } from 'react';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

export interface EditorAutoFocusOptions {
  /** Focus the editor when the component mounts (useful for page navigation) */
  focusOnMount?: boolean;
  /** Focus the editor when the window gains focus (useful for tab switching) */
  focusOnWindowFocus?: boolean;
  /** Delay in milliseconds before focusing (to ensure editor is ready) */
  delay?: number;
}

/**
 * Custom hook that automatically focuses the editor in various scenarios
 * Places the cursor at the end of the content, as discussed in:
 * https://github.com/udecode/plate/discussions/804
 *
 * This improves the user experience by:
 * - Automatically focusing the editor when navigating to a page with an editor
 * - Automatically focusing the editor when switching back from another tab/window
 * - Positioning the cursor at the end of the content for immediate typing
 * - Handling focus gracefully without errors if the editor is unmounted
 *
 * @param editor - The Slate/Plate editor instance
 * @param options - Configuration options for autofocus behavior
 */
export function useEditorAutoFocus(
  editor: any,
  options: EditorAutoFocusOptions = {}
) {
  const {
    focusOnMount = false,
    focusOnWindowFocus = true,
    delay = 100
  } = options;
  // Helper function to focus the editor
  const focusEditor = () => {
    try {
      // Check if the editor is still mounted and focusable
      if (editor && !ReactEditor.isFocused(editor)) {
        // Focus the editor first
        ReactEditor.focus(editor);

        // Then move cursor to the end of the content
        // This uses the solution from the GitHub discussion
        Transforms.select(editor, Editor.end(editor, []));
      }
    } catch (error) {
      // Silently handle any errors (e.g., if editor is unmounted)
      console.debug('Editor autofocus error:', error);
    }
  };

  // Focus on mount (for page navigation)
  useEffect(() => {
    if (!editor || !focusOnMount) return;

    const timeoutId = setTimeout(() => {
      focusEditor();
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [editor, focusOnMount, delay]);

  // Focus on window focus (for tab switching)
  useEffect(() => {
    if (!editor || !focusOnWindowFocus) return;

    const handleWindowFocus = () => {
      setTimeout(() => {
        focusEditor();
      }, delay);
    };

    // Add the window focus event listener
    window.addEventListener('focus', handleWindowFocus);

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [editor, focusOnWindowFocus, delay]);
}
